import { supabase } from './supabase';

type RpcResult<T>={data:T|null;error:{message:string}|null};
export type JobSharedAttachment={id:string;file_name:string;mime_type:string|null;size_bytes:number|null;storage_bucket:string;storage_path:string;created_at:string;uploaded_by_name:string;attachment_kind:'shared_document'|'quality_control_photo'|string;quality_checkpoint:'post_install'|'pre_teardown'|'receiving_inspection'|null;damage_flagged:boolean;damage_attribution:'none'|'crew'|'customer'|'carrier'|string};
export type JobSharedAttachmentsWorkspace={contract_version:string;job_id:string;organization_id:string;storage_bucket:string;capabilities:{upload:boolean;delete:boolean;download:boolean};attachments:JobSharedAttachment[]};

async function rpc<T>(name:string,args:Record<string,unknown>):Promise<RpcResult<T>>{const r=await supabase.rpc(name,args);return{data:(r.data??null) as T|null,error:r.error?{message:r.error.message}:null}}
function safeName(name:string){return name.replace(/[^a-zA-Z0-9._-]+/g,'_').slice(-120)||'attachment'}

export const jobSharedAttachmentsApi={
  forJob:(jobId:string)=>rpc<JobSharedAttachmentsWorkspace>('get_job_shared_attachments',{p_job_id:jobId}),
  async upload(organizationId:string,jobId:string,file:File):Promise<RpcResult<string>>{
    if(file.size>20*1024*1024)return{data:null,error:{message:'Attachment exceeds the 20 MB limit.'}};
    const bucket='job-shared-attachments';
    const path=`${organizationId}/${jobId}/${Date.now()}-${crypto.randomUUID()}-${safeName(file.name)}`;
    const up=await supabase.storage.from(bucket).upload(path,file,{contentType:file.type||'application/octet-stream',upsert:false});
    if(up.error)return{data:null,error:{message:up.error.message}};
    const reg=await rpc<string>('register_job_shared_attachment',{p_job_id:jobId,p_storage_path:path,p_file_name:file.name,p_mime_type:file.type||null,p_size_bytes:file.size});
    if(reg.error)await supabase.storage.from(bucket).remove([path]);
    return reg;
  },
  async uploadQualityPhoto(organizationId:string,jobId:string,checkpoint:string,file:File):Promise<RpcResult<string>>{
    if(!file.type.startsWith('image/'))return{data:null,error:{message:'Quality Control evidence must be an image.'}};
    if(file.size>20*1024*1024)return{data:null,error:{message:'Photo exceeds the 20 MB limit.'}};
    const bucket='job-shared-attachments';
    const path=`${organizationId}/${jobId}/${Date.now()}-${crypto.randomUUID()}-${safeName(file.name)}`;
    const up=await supabase.storage.from(bucket).upload(path,file,{contentType:file.type,upsert:false});
    if(up.error)return{data:null,error:{message:up.error.message}};
    const reg=await rpc<string>('register_field_quality_photo',{p_job_id:jobId,p_checkpoint:checkpoint,p_storage_path:path,p_file_name:file.name,p_mime_type:file.type,p_size_bytes:file.size});
    if(reg.error)await supabase.storage.from(bucket).remove([path]);
    return reg;
  },
  async download(attachment:JobSharedAttachment):Promise<RpcResult<Blob>>{
    const r=await supabase.storage.from(attachment.storage_bucket).download(attachment.storage_path);
    return{data:r.data??null,error:r.error?{message:r.error.message}:null};
  },
  async remove(attachment:JobSharedAttachment):Promise<RpcResult<string>>{
    const r=await rpc<string>('delete_job_shared_attachment',{p_attachment_id:attachment.id});
    if(r.error)return r;
    const path=r.data||attachment.storage_path;
    const removed=await supabase.storage.from(attachment.storage_bucket).remove([path]);
    if(removed.error)return{data:path,error:{message:`Attachment record removed, but stored file cleanup failed: ${removed.error.message}`}};
    return{data:path,error:null};
  }
};
